import React, { useState } from 'react';
import { API_URL, descargarBase64 } from '../constants';
import { SeccionLabel } from './shared';
import { parsearPlantillaXLSX } from '../utils/importacionParser';

// ════════════════════════════════════════════════════════════════════════════
// IMPORTACIÓN INTELLISIS — FASE 2 (previsualización) + FASE 3 bloque G (Apply)
// + bloque H (reingresos pendientes)
// ════════════════════════════════════════════════════════════════════════════
// Esta pantalla parsea el XLSX en el navegador (parsearPlantillaXLSX, sin
// reimplementar lectura de XLSX aquí) y envía las filas normalizadas a
// api/importacion/previsualizar (dry-run, solo lectura). El botón "Aplicar
// importación" se habilita después de un dry-run exitoso, sin errores
// bloqueantes y con AL MENOS UNA ACCIÓN VÁLIDA que realizar (nuevos>0 O
// reingresos>0 — decisión de producto: un lote compuesto solo por
// IGNORADO_EXISTENTE no tiene nada que aplicar, pero un lote con reingresos
// SÍ, aunque nunca reactive a nadie automáticamente). Llama
// api/importacion/aplicar enviando EXCLUSIVAMENTE {filename, filas} (las
// filas normalizadas originales del parser, nunca clasificación/
// datos_transformados/resumen calculados en el frontend). El backend
// SIEMPRE recalcula todo de nuevo antes de escribir — esta pantalla nunca
// decide qué se crea, solo dispara la solicitud y muestra el resultado. Un
// REINGRESO detectado NUNCA reactiva al empleado ni modifica EMPLEADOS: solo
// registra/actualiza un pendiente para revisión futura de Recursos Humanos
// — la UI nunca debe sugerir que Apply reingresa directamente a nadie.

// ─── Lógica pura (sin estado de React, sin fetch) — exportada para pruebas ──

export const FILTROS_PREVIEW = [
  { key: 'todas', label: 'Todas', clasificacion: null },
  { key: 'nuevos', label: 'Nuevos', clasificacion: 'NUEVO' },
  { key: 'existentes', label: 'Existentes', clasificacion: 'IGNORADO_EXISTENTE' },
  { key: 'reingresos', label: 'Reingresos', clasificacion: 'REINGRESO' },
  { key: 'errores', label: 'Errores', clasificacion: 'ERROR' }
];

export function filtrarFilasPreview(filas, filtroKey) {
  if (!Array.isArray(filas)) return [];
  const filtro = FILTROS_PREVIEW.find(f => f.key === filtroKey);
  if (!filtro || !filtro.clasificacion) return filas;
  return filas.filter(f => f.clasificacion === filtro.clasificacion);
}

export function contarFilasPorClasificacion(filas, clasificacion) {
  if (!Array.isArray(filas)) return 0;
  return filas.filter(f => f.clasificacion === clasificacion).length;
}

// Nombre solo para presentación — no modifica datos_normalizados.
export function construirNombreCompleto(datosNormalizados) {
  if (!datosNormalizados) return '';
  return [datosNormalizados.Nombres, datosNormalizados.Apellido_Paterno, datosNormalizados.Apellido_Materno]
    .map(v => (v === undefined || v === null ? '' : String(v)).trim())
    .filter(Boolean)
    .join(' ');
}

export function listarObservaciones(filaPreview) {
  if (!filaPreview) return [];
  const obs = [];
  (filaPreview.errores || []).forEach(e => obs.push({ tipo: 'error', codigo: e.codigo, mensaje: e.mensaje }));
  (filaPreview.advertencias || []).forEach(a => obs.push({ tipo: 'advertencia', codigo: a.codigo, mensaje: a.mensaje }));
  (filaPreview.informativos || []).forEach(i => obs.push({ tipo: 'informativo', codigo: i.codigo, mensaje: i.mensaje }));
  return obs;
}

export const ETIQUETA_CLASIFICACION = {
  NUEVO: 'Nuevo',
  IGNORADO_EXISTENTE: 'Ya registrado',
  REINGRESO: 'Reingreso requerido',
  ERROR: 'Error'
};

export const BADGE_CLASE_CLASIFICACION = {
  NUEVO: 'badge-success',
  IGNORADO_EXISTENTE: 'badge-warning',
  REINGRESO: 'badge-warning',
  ERROR: 'badge-danger'
};

export function botonPrevisualizarDeshabilitado(archivo, procesando, aplicando) {
  return !archivo || !!procesando || !!aplicando;
}

// Distingue error de contrato backend (success=false + error_estructural) de
// cualquier otro success=false sin ese campo — nunca inventa un mensaje si el
// backend ya trae uno.
export function construirMensajeErrorContrato(respuestaJson) {
  if (respuestaJson && respuestaJson.error_estructural && respuestaJson.error_estructural.mensaje) {
    return respuestaJson.error_estructural.mensaje;
  }
  if (respuestaJson && respuestaJson.error) return respuestaJson.error;
  return 'El servidor respondió con un error no especificado.';
}

// Orquestación pura de la previsualización (parser → POST → resultado
// clasificado). Recibe parsear/fetchImpl como dependencias inyectables SOLO
// para poder probarla con Jest sin renderizar el componente ni depender del
// fetch global — en producción el componente la llama sin overrides y usa
// parsearPlantillaXLSX/fetch reales.
export async function ejecutarPrevisualizacion(archivo, { parsear = parsearPlantillaXLSX, fetchImpl = fetch } = {}) {
  const resultadoParser = await parsear(archivo);

  if (!resultadoParser.success) {
    // Error estructural LOCAL (parser) — el backend nunca se llama.
    return { tipo: 'error_local', mensaje: resultadoParser.error_estructural.mensaje };
  }

  let respuestaHttp;
  try {
    respuestaHttp = await fetchImpl(API_URL + '?path=api/importacion/previsualizar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filas: resultadoParser.filas })
    });
  } catch (err) {
    // Error de RED/TRANSPORTE — el fetch en sí falló.
    return { tipo: 'error_transporte', mensaje: 'No se pudo conectar con el servidor. Verifica tu conexión e intenta nuevamente.' };
  }

  let data;
  try {
    data = await respuestaHttp.json();
  } catch (err) {
    return { tipo: 'error_transporte', mensaje: 'El servidor respondió con un formato inesperado.' };
  }

  if (!data.success) {
    // Error estructural del BACKEND (contrato de importación inválido).
    return { tipo: 'error_contrato', mensaje: construirMensajeErrorContrato(data) };
  }

  // filasParser: las filas normalizadas del parser (no las clasificadas por
  // el backend) — se conservan aquí para que, si el usuario decide Aplicar
  // más adelante, se reenvíe EXACTAMENTE lo mismo que ya se previsualizó,
  // sin volver a parsear el XLSX ni construir un payload distinto.
  return { tipo: 'ok', preview: data, filasParser: resultadoParser.filas };
}

// Orquestación pura de Apply (POST → resultado crudo). Misma forma que
// ejecutarPrevisualizacion — separa el transporte (aquí) de la
// interpretación del resultado (interpretarResultadoApply). Nunca envía
// clasificación/datos_transformados/resumen calculados en el frontend: solo
// {filename, filas}, las mismas filas normalizadas que ya validó el dry-run.
export async function ejecutarApply(filename, filas, { fetchImpl = fetch } = {}) {
  let respuestaHttp;
  try {
    respuestaHttp = await fetchImpl(API_URL + '?path=api/importacion/aplicar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, filas })
    });
  } catch (err) {
    return { tipo: 'error_transporte', mensaje: 'No se pudo conectar con el servidor. Verifica tu conexión e intenta nuevamente.' };
  }

  let data;
  try {
    data = await respuestaHttp.json();
  } catch (err) {
    return { tipo: 'error_transporte', mensaje: 'El servidor respondió con un formato inesperado.' };
  }

  return { tipo: 'ok', respuesta: data };
}

// Condiciones para habilitar "Aplicar importación" — única fuente de verdad
// para el botón. NO recalcula clasificaciones: solo lee preview.resumen (ya
// calculado por el backend) y confirma que el preview sigue vigente para el
// archivo actualmente seleccionado (comparación por referencia del objeto
// File — cualquier nueva selección, aunque sea el mismo nombre de archivo,
// genera un objeto File distinto, invalidando el preview anterior).
export function puedeAplicarImportacion({ preview, filasPreparadas, archivo, archivoPreview, procesando, aplicando }) {
  if (procesando || aplicando) return false;
  if (!archivo) return false;
  if (!preview || preview.success !== true) return false;
  if (!filasPreparadas) return false;
  if (archivoPreview !== archivo) return false;
  if (!preview.resumen || preview.resumen.errores_bloqueantes > 0) return false;
  // Al menos una acción persistente que realizar: crear NUEVOs o registrar
  // pendientes de REINGRESO. Un lote compuesto solo por IGNORADO_EXISTENTE
  // (nuevos=0 y reingresos=0) no tiene nada que aplicar.
  if (preview.resumen.nuevos === 0 && preview.resumen.reingresos === 0) return false;
  return true;
}

// Texto de ayuda que explica por qué el botón está deshabilitado — nunca
// vacío salvo cuando SÍ puede aplicarse (en ese caso no hace falta explicar
// nada). Mismo criterio que puedeAplicarImportacion, en el mismo orden.
export function construirMensajeAyudaApply({ preview, filasPreparadas, archivo, archivoPreview, procesando, aplicando }) {
  if (procesando) return 'Espera a que termine la previsualización.';
  if (aplicando) return 'Aplicando importación...';
  if (!archivo) return 'Selecciona un archivo para continuar.';
  if (!preview || !filasPreparadas || archivoPreview !== archivo) return 'Vuelve a previsualizar este archivo antes de aplicar.';
  if (!preview.resumen || preview.resumen.errores_bloqueantes > 0) return 'Corrige los errores bloqueantes y vuelve a previsualizar antes de aplicar.';
  if (preview.resumen.nuevos === 0 && preview.resumen.reingresos === 0) return 'No hay empleados nuevos ni reingresos por registrar.';
  return '';
}

// Mensaje de confirmación explícita (window.confirm) — mismo patrón ya usado
// en Accesos/Bajas/Empleados del proyecto. Nunca promete "todo o nada" ni
// rollback automático.
export function construirResumenConfirmacion(resumen) {
  if (!resumen) return '';
  return [
    `Se crearán ${resumen.nuevos} empleados nuevos.`,
    `${resumen.existentes_ignorados} empleados ya registrados serán ignorados.`,
    `${resumen.reingresos} reingresos quedarán pendientes de revisión por Recursos Humanos (no se reactivan automáticamente).`,
    'La información será validada nuevamente antes de escribir.',
    'Esta acción puede modificar EMPLEADOS y generar expedientes.'
  ].join('\n');
}

// Traduce la respuesta cruda de api/importacion/aplicar a una forma
// normalizada para renderizar, cubriendo todos los estados/códigos que el
// orquestador puede devolver. Centraliza la interpretación para que sea
// probable de forma aislada, sin renderizar nada.
export function interpretarResultadoApply(respuesta) {
  if (!respuesta) {
    return { tipo: 'error_generico', titulo: 'No se pudo aplicar la importación', mensaje: 'El servidor no devolvió una respuesta válida.', resumen: null };
  }

  const estado = respuesta.estado_ejecucion;
  const codigo = respuesta.error_estructural && respuesta.error_estructural.codigo;
  const base = { resumen: respuesta.resumen || null, id_importacion: respuesta.id_importacion || null, filas: respuesta.filas || [] };

  if (estado === 'COMPLETA') {
    return Object.assign({}, base, {
      tipo: 'completa',
      titulo: 'Importación completada.',
      snapshotGenerado: !!(respuesta.snapshot && respuesta.snapshot.spreadsheet_id)
    });
  }

  if (estado === 'PARCIAL_CON_FALLO') {
    return Object.assign({}, base, {
      tipo: 'parcial',
      titulo: 'Importación parcialmente completada.',
      mensaje: 'La importación se detuvo al encontrar un error. Algunos empleados no fueron procesados. No se realizó rollback automático. Debe volver a previsualizar antes de intentar nuevamente.'
    });
  }

  if (codigo === 'IMPORTACION_EN_PROCESO') {
    return Object.assign({}, base, {
      tipo: 'en_proceso',
      titulo: 'Importación en proceso',
      mensaje: 'Ya existe otra importación en proceso. Intente nuevamente cuando finalice.'
    });
  }

  if (estado === 'BLOQUEADA_POR_ERRORES') {
    return Object.assign({}, base, {
      tipo: 'bloqueada',
      titulo: 'No se pudo aplicar',
      mensaje: 'El estado de SIG-RH cambió desde la previsualización o el archivo ya no puede aplicarse. No se creó ningún empleado.'
    });
  }

  if (estado === 'SNAPSHOT_FALLIDO') {
    return Object.assign({}, base, {
      tipo: 'snapshot_fallido',
      titulo: 'No se pudo crear el respaldo',
      mensaje: 'No se pudo crear el respaldo previo. La importación fue cancelada y ningún empleado fue creado.'
    });
  }

  if (estado === 'FALLO_RECALCULO') {
    return Object.assign({}, base, {
      tipo: 'fallo_recalculo',
      titulo: 'No se pudo validar el archivo',
      mensaje: 'No fue posible volver a validar la información antes de importar. No se realizaron cambios.'
    });
  }

  if (codigo === 'REGISTRO_CORRIDA_FALLIDO' || codigo === 'REGISTRO_APLICANDO_FALLIDO') {
    const menciónSnapshot = (codigo === 'REGISTRO_APLICANDO_FALLIDO' && respuesta.snapshot && respuesta.snapshot.spreadsheet_id)
      ? ' El respaldo sí fue generado, pero no se inició la creación.'
      : '';
    return Object.assign({}, base, {
      tipo: 'registro_fallido',
      titulo: 'No se pudo iniciar la importación',
      mensaje: 'La importación fue cancelada antes de crear empleados porque no pudo registrarse correctamente la corrida.' + menciónSnapshot
    });
  }

  // Cualquier otro caso no anticipado explícitamente (ej.
  // USUARIO_EJECUTOR_NO_IDENTIFICADO, PAYLOAD_IMPORTACION_INVALIDO,
  // EXCEPCION_INESPERADA) — mensaje genérico sin ocultar el mensaje real del
  // backend si lo trae.
  return Object.assign({}, base, {
    tipo: 'error_generico',
    titulo: 'No se pudo aplicar la importación',
    mensaje: (respuesta.error_estructural && respuesta.error_estructural.mensaje) || 'Ocurrió un error inesperado.'
  });
}

// Único caso en el que el preview/filasPreparadas se CONSERVAN después de un
// intento de Apply: IMPORTACION_EN_PROCESO (no se escribió nada, ni siquiera
// se recalculó — el usuario puede reintentar el mismo preview más tarde).
// En cualquier otro resultado (éxito completo, parcial, o cualquier fallo
// con o sin escrituras) se exige un nuevo preview antes de poder aplicar de
// nuevo — nunca se reintenta ciegamente el mismo Apply.
export function debeInvalidarPreviewDespuesApply(tipoResultado) {
  return tipoResultado !== 'en_proceso';
}

export const ETIQUETA_RESULTADO_APPLY = {
  CREADO: 'Creado',
  IGNORADO_EXISTENTE: 'Ya registrado — no modificado',
  REINGRESO_PENDIENTE: 'Reingreso pendiente — requiere revisión de RH',
  ERROR: 'Error',
  FALLIDO: 'Falló',
  NO_PROCESADO: 'No procesado'
};

export const BADGE_CLASE_RESULTADO_APPLY = {
  CREADO: 'badge-success',
  IGNORADO_EXISTENTE: 'badge-warning',
  REINGRESO_PENDIENTE: 'badge-warning',
  ERROR: 'badge-danger',
  FALLIDO: 'badge-danger',
  NO_PROCESADO: 'badge-warning'
};

export function mapearResultadoApplyFila(filaApply) {
  if (!filaApply) return null;
  return {
    etiqueta: ETIQUETA_RESULTADO_APPLY[filaApply.resultado] || filaApply.resultado,
    claseBadge: BADGE_CLASE_RESULTADO_APPLY[filaApply.resultado] || ''
  };
}

// Campos de datos_transformados que interesa mostrar destacados para NUEVO
// (jerarquía resuelta), en el orden en que se listaron en el requerimiento.
const CAMPOS_TRANSFORMADOS_DESTACADOS = [
  ['sucursal_nomina', 'Sucursal (nómina)'],
  ['sucursal_fisica', 'Sucursal física'],
  ['area', 'Área'],
  ['puesto', 'Puesto'],
  ['posicion_nodo_id', 'Posición / Nodo'],
  ['turno', 'Turno'],
  ['jefe_directo_id', 'Jefe directo (ID)'],
  ['tipo_contrato', 'Tipo de contrato']
];

// ─── Componente ──────────────────────────────────────────────────────────

export function ImportacionIntellisis() {
  const [descargando, setDescargando] = useState(false);

  const [archivo, setArchivo] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [errorLocal, setErrorLocal] = useState(null);
  const [errorContrato, setErrorContrato] = useState(null);
  const [errorTransporte, setErrorTransporte] = useState(null);
  const [preview, setPreview] = useState(null);
  const [filtro, setFiltro] = useState('todas');
  const [filaExpandida, setFilaExpandida] = useState(null);

  const [filasPreparadas, setFilasPreparadas] = useState(null);
  const [archivoPreview, setArchivoPreview] = useState(null);
  const [aplicando, setAplicando] = useState(false);
  const [resultadoApply, setResultadoApply] = useState(null);
  const [errorApplyTransporte, setErrorApplyTransporte] = useState(null);

  const handleDescargarPlantilla = async () => {
    if (descargando) return;
    setDescargando(true);
    try {
      const res = await fetch(API_URL + '?path=api/importacion/plantilla');
      const data = await res.json();
      if (data.success) {
        descargarBase64(data.base64, data.filename);
        alert('✅ ' + (data.mensaje || 'Plantilla descargada correctamente.'));
      } else {
        alert('❌ Error: ' + data.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setDescargando(false);
    }
  };

  const handleSeleccionarArchivo = (e) => {
    const file = (e.target.files && e.target.files[0]) || null;
    setArchivo(file);
    // Un archivo nuevo invalida cualquier previsualización y resultado de Apply previos.
    setPreview(null);
    setErrorLocal(null);
    setErrorContrato(null);
    setErrorTransporte(null);
    setFiltro('todas');
    setFilaExpandida(null);
    setFilasPreparadas(null);
    setArchivoPreview(null);
    setResultadoApply(null);
    setErrorApplyTransporte(null);
  };

  const handlePrevisualizar = async () => {
    if (!archivo || procesando || aplicando) return;

    // Limpia estado de una previsualización anterior antes de empezar.
    setPreview(null);
    setErrorLocal(null);
    setErrorContrato(null);
    setErrorTransporte(null);
    setFiltro('todas');
    setFilaExpandida(null);
    setFilasPreparadas(null);
    setArchivoPreview(null);
    setResultadoApply(null);
    setErrorApplyTransporte(null);
    setProcesando(true);

    try {
      const resultado = await ejecutarPrevisualizacion(archivo);
      if (resultado.tipo === 'error_local') setErrorLocal(resultado.mensaje);
      else if (resultado.tipo === 'error_transporte') setErrorTransporte(resultado.mensaje);
      else if (resultado.tipo === 'error_contrato') setErrorContrato(resultado.mensaje);
      else {
        setPreview(resultado.preview);
        setFilasPreparadas(resultado.filasParser);
        setArchivoPreview(archivo);
      }
    } finally {
      setProcesando(false);
    }
  };

  const handleAplicarClick = () => {
    if (!puedeAplicarImportacion({ preview, filasPreparadas, archivo, archivoPreview, procesando, aplicando })) return;
    const mensaje = construirResumenConfirmacion(preview.resumen);
    if (!window.confirm(mensaje)) return;
    handleAplicar();
  };

  const handleAplicar = async () => {
    setResultadoApply(null);
    setErrorApplyTransporte(null);
    setAplicando(true);
    try {
      const resultado = await ejecutarApply(archivo.name, filasPreparadas);
      if (resultado.tipo === 'error_transporte') {
        setErrorApplyTransporte(resultado.mensaje);
        return;
      }
      const interpretado = interpretarResultadoApply(resultado.respuesta);
      setResultadoApply(interpretado);
      if (debeInvalidarPreviewDespuesApply(interpretado.tipo)) {
        setFilasPreparadas(null);
      }
    } finally {
      setAplicando(false);
    }
  };

  const filasFiltradas = preview ? filtrarFilasPreview(preview.filas, filtro) : [];
  const resultadoApplyPorFila = {};
  if (resultadoApply && Array.isArray(resultadoApply.filas)) {
    resultadoApply.filas.forEach(f => { resultadoApplyPorFila[f.fila_excel] = f; });
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#1d3d6b' }}>Importación Intellisis</h1>
        <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '2px' }}>
          Preparación de la migración inicial de empleados desde Intellisis hacia SIG-RH.
        </p>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: '20px' }}>
        <SeccionLabel icon="ti-file-spreadsheet">Plantilla de migración</SeccionLabel>

        <p style={{ color: '#4b5563', fontSize: '13px', lineHeight: 1.6, marginBottom: '16px' }}>
          Descarga la plantilla oficial en Excel para preparar manualmente los datos de la migración inicial.
          Incluye la hoja <strong>EMPLEADOS_IMPORTAR</strong> para capturar la información,{' '}
          <strong>CATALOGOS_SIGRH</strong> con los IDs vigentes de Sucursal, Ubicación física, Centro de
          Costos, Nodo, Puesto y Turno, e <strong>INSTRUCCIONES</strong> con el detalle de cómo llenarla.
        </p>

        <button
          className="btn-primary"
          onClick={handleDescargarPlantilla}
          disabled={descargando}
        >
          {descargando ? 'Generando...' : 'Descargar plantilla XLSX'}
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <SeccionLabel icon="ti-list-search">Previsualizar importación</SeccionLabel>

        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
          <p style={{ fontSize: '12px', color: '#1e40af', margin: 0 }}>
            🔒 Esta previsualización no modifica información en SIG-RH.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <input
            type="file"
            accept=".xlsx"
            onChange={handleSeleccionarArchivo}
            disabled={procesando || aplicando}
            style={{ fontSize: '13px' }}
          />
          {archivo && (
            <span style={{ fontSize: '12px', color: '#4b5563' }}>
              <i className="ti ti-file-spreadsheet"></i> {archivo.name}
            </span>
          )}
          <button
            className="btn-primary"
            onClick={handlePrevisualizar}
            disabled={botonPrevisualizarDeshabilitado(archivo, procesando, aplicando)}
          >
            {procesando ? 'Procesando...' : 'Previsualizar importación'}
          </button>
        </div>

        {errorLocal && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#92400e', margin: '0 0 4px' }}>
              Error en el archivo
            </p>
            <p style={{ fontSize: '12px', color: '#92400e', margin: 0 }}>{errorLocal}</p>
          </div>
        )}

        {errorContrato && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#991b1b', margin: '0 0 4px' }}>
              Error del servidor
            </p>
            <p style={{ fontSize: '12px', color: '#991b1b', margin: 0 }}>{errorContrato}</p>
          </div>
        )}

        {errorTransporte && (
          <div style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#374151', margin: '0 0 4px' }}>
              Error de conexión
            </p>
            <p style={{ fontSize: '12px', color: '#374151', margin: 0 }}>{errorTransporte}</p>
          </div>
        )}

        {preview && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '16px' }}>
              <ResumenCard label="Total filas" valor={preview.resumen.total_filas} />
              <ResumenCard label="Nuevos" valor={preview.resumen.nuevos} color="#065f46" />
              <ResumenCard label="Ya existentes" valor={preview.resumen.existentes_ignorados} color="#92400e" />
              <ResumenCard label="Reingresos" valor={preview.resumen.reingresos} color="#92400e" />
              <ResumenCard label="Errores" valor={preview.resumen.errores_bloqueantes} color="#991b1b" />
              <ResumenCard label="Con advertencias" valor={preview.resumen.filas_con_advertencias} color="#6b7280" />
            </div>

            {preview.resumen.errores_bloqueantes > 0 ? (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
                <p style={{ fontSize: '12px', color: '#991b1b', margin: 0, fontWeight: 600 }}>
                  El archivo contiene errores bloqueantes. Corrige la plantilla y vuelve a previsualizar.
                </p>
              </div>
            ) : (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
                <p style={{ fontSize: '12px', color: '#065f46', margin: 0, fontWeight: 600 }}>
                  Previsualización lista. No se ha modificado ningún empleado.
                </p>
              </div>
            )}

            <div style={{ background: '#f8fafc', border: '1px solid #e8ecf0', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
              <p style={{ fontSize: '12px', fontWeight: 700, color: '#1d3d6b', margin: '0 0 6px' }}>
                Qué se va a hacer si aplicas esta importación
              </p>
              <ul style={{ fontSize: '12px', color: '#374151', margin: '0 0 10px', paddingLeft: '18px', lineHeight: 1.7 }}>
                <li><strong>{preview.resumen.nuevos}</strong> empleados nuevos podrían crearse.</li>
                <li><strong>{preview.resumen.existentes_ignorados}</strong> ya existentes serán ignorados (no se modifican).</li>
                <li><strong>{preview.resumen.reingresos}</strong> reingresos quedarán registrados como pendientes para revisión de Recursos Humanos (no se reactivan automáticamente).</li>
                <li><strong>{preview.resumen.errores_bloqueantes}</strong> filas con errores.</li>
              </ul>
              <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 10px' }}>
                La aplicación volverá a validar el archivo antes de crear empleados.
              </p>

              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}>
                <p style={{ fontSize: '12px', color: '#991b1b', margin: 0, fontWeight: 700 }}>
                  ⚠️ Esta acción sí modificará SIG-RH.
                </p>
              </div>

              <button
                className="btn-primary"
                onClick={handleAplicarClick}
                disabled={!puedeAplicarImportacion({ preview, filasPreparadas, archivo, archivoPreview, procesando, aplicando })}
              >
                {aplicando ? 'Aplicando...' : 'Aplicar importación'}
              </button>
              {(() => {
                const ayuda = construirMensajeAyudaApply({ preview, filasPreparadas, archivo, archivoPreview, procesando, aplicando });
                return ayuda ? (
                  <p style={{ fontSize: '11px', color: '#6b7280', margin: '8px 0 0' }}>{ayuda}</p>
                ) : null;
              })()}
            </div>

            {errorApplyTransporte && (
              <div style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
                <p style={{ fontSize: '12px', fontWeight: 700, color: '#374151', margin: '0 0 4px' }}>
                  Error de conexión al aplicar
                </p>
                <p style={{ fontSize: '12px', color: '#374151', margin: 0 }}>{errorApplyTransporte}</p>
              </div>
            )}

            {resultadoApply && (
              <div
                style={{
                  background: resultadoApply.tipo === 'completa' ? '#f0fdf4' : resultadoApply.tipo === 'parcial' ? '#fffbeb' : '#fef2f2',
                  border: `1px solid ${resultadoApply.tipo === 'completa' ? '#bbf7d0' : resultadoApply.tipo === 'parcial' ? '#fde68a' : '#fecaca'}`,
                  borderRadius: '8px',
                  padding: '14px 16px',
                  marginBottom: '16px'
                }}
              >
                <p style={{
                  fontSize: '13px', fontWeight: 700, margin: '0 0 6px',
                  color: resultadoApply.tipo === 'completa' ? '#065f46' : resultadoApply.tipo === 'parcial' ? '#92400e' : '#991b1b'
                }}>
                  {resultadoApply.titulo}
                </p>
                {resultadoApply.mensaje && (
                  <p style={{ fontSize: '12px', color: '#374151', margin: '0 0 8px', whiteSpace: 'pre-line' }}>
                    {resultadoApply.mensaje}
                  </p>
                )}
                {resultadoApply.resumen && (resultadoApply.tipo === 'completa' || resultadoApply.tipo === 'parcial') && (
                  <ul style={{ fontSize: '12px', color: '#374151', margin: '0 0 8px', paddingLeft: '18px', lineHeight: 1.7 }}>
                    <li><strong>{resultadoApply.resumen.creados}</strong> empleados creados.</li>
                    <li><strong>{resultadoApply.resumen.existentes_ignorados}</strong> ya existentes ignorados (no modificados).</li>
                    <li><strong>{resultadoApply.resumen.reingresos}</strong> reingresos quedaron registrados como pendientes para revisión de RH (no se reactivaron automáticamente).</li>
                    {resultadoApply.tipo === 'parcial' && <li>Algunas filas no fueron procesadas por detenerse la importación en el primer error. No hubo rollback automático.</li>}
                  </ul>
                )}
                {resultadoApply.tipo === 'completa' && (
                  <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                    ID de importación: <code>{resultadoApply.id_importacion}</code> — Snapshot generado: {resultadoApply.snapshotGenerado ? 'sí' : 'no'}
                  </p>
                )}
                {resultadoApply.tipo === 'parcial' && (
                  <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                    ID de importación: <code>{resultadoApply.id_importacion}</code> — Debe volver a previsualizar antes de intentar nuevamente.
                  </p>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
              {FILTROS_PREVIEW.map(f => {
                const cuenta = f.clasificacion ? contarFilasPorClasificacion(preview.filas, f.clasificacion) : preview.filas.length;
                const activo = filtro === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setFiltro(f.key)}
                    className={activo ? 'btn-primary' : 'btn-secondary'}
                    style={{ fontSize: '11px', padding: '6px 12px' }}
                  >
                    {f.label} ({cuenta})
                  </button>
                );
              })}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="tabla">
                <thead>
                  <tr>
                    <th></th>
                    <th>Fila Excel</th>
                    <th>ID Empleado</th>
                    <th>Nombre</th>
                    <th>CURP</th>
                    <th>Clasificación</th>
                    {resultadoApply && <th>Resultado Apply</th>}
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filasFiltradas.length === 0 ? (
                    <tr>
                      <td colSpan={resultadoApply ? 8 : 7} style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>
                        Ninguna fila coincide con este filtro.
                      </td>
                    </tr>
                  ) : (
                    filasFiltradas.map(fila => {
                      const abierta = filaExpandida === fila.fila_excel;
                      const observaciones = listarObservaciones(fila);
                      return (
                        <React.Fragment key={fila.fila_excel}>
                          <tr>
                            <td>
                              <button
                                className="btn-exp"
                                onClick={() => setFilaExpandida(abierta ? null : fila.fila_excel)}
                              >
                                <i className={`ti ${abierta ? 'ti-chevron-up' : 'ti-chevron-down'}`}></i>
                              </button>
                            </td>
                            <td>{fila.fila_excel}</td>
                            <td>{fila.identidad.id_empleado}</td>
                            <td>{construirNombreCompleto(fila.datos_normalizados)}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>{fila.identidad.curp}</td>
                            <td>
                              <span className={`badge ${BADGE_CLASE_CLASIFICACION[fila.clasificacion] || ''}`}>
                                {ETIQUETA_CLASIFICACION[fila.clasificacion] || fila.clasificacion}
                              </span>
                            </td>
                            {resultadoApply && (
                              <td>
                                {(() => {
                                  const mapeado = mapearResultadoApplyFila(resultadoApplyPorFila[fila.fila_excel]);
                                  return mapeado ? (
                                    <span className={`badge ${mapeado.claseBadge}`}>{mapeado.etiqueta}</span>
                                  ) : (
                                    <span style={{ color: '#9ca3af' }}>—</span>
                                  );
                                })()}
                              </td>
                            )}
                            <td style={{ maxWidth: '320px' }}>
                              {observaciones.length === 0 ? (
                                <span style={{ color: '#9ca3af' }}>—</span>
                              ) : (
                                observaciones.map((o, i) => (
                                  <div key={i} style={{ fontSize: '11px', color: o.tipo === 'error' ? '#991b1b' : o.tipo === 'advertencia' ? '#92400e' : '#6b7280' }}>
                                    {o.codigo ? <code>{o.codigo}</code> : null} {o.mensaje}
                                  </div>
                                ))
                              )}
                            </td>
                          </tr>
                          {abierta && (
                            <tr>
                              <td colSpan={resultadoApply ? 8 : 7} style={{ background: '#fafbfc', padding: '16px' }}>
                                <DetalleFila fila={fila} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ResumenCard({ label, valor, color }) {
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e8ecf0', borderRadius: '8px', padding: '10px 12px' }}>
      <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: color || '#1d3d6b' }}>{valor}</div>
    </div>
  );
}

// Detalle expandible de una fila: datos_normalizados, datos_transformados (si
// existe) y el 100% de errores/advertencias/informativos. Sin edición.
function DetalleFila({ fila }) {
  const observaciones = listarObservaciones(fila);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
      <div>
        <p style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>
          Datos normalizados (archivo)
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: '11px', maxHeight: '260px', overflowY: 'auto' }}>
          {Object.entries(fila.datos_normalizados || {}).map(([campo, valor]) => (
            <React.Fragment key={campo}>
              <div style={{ color: '#9ca3af' }}>{campo}</div>
              <div style={{ color: '#1f2937', wordBreak: 'break-word' }}>{valor === '' || valor === null || valor === undefined ? '—' : String(valor)}</div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div>
        {fila.clasificacion === 'NUEVO' && fila.datos_transformados ? (
          <>
            <p style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>
              Jerarquía resuelta (datos_transformados)
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: '11px', marginBottom: '16px' }}>
              {CAMPOS_TRANSFORMADOS_DESTACADOS.map(([campo, etiqueta]) => (
                <React.Fragment key={campo}>
                  <div style={{ color: '#9ca3af' }}>{etiqueta}</div>
                  <div style={{ color: '#1f2937', wordBreak: 'break-word' }}>
                    {fila.datos_transformados[campo] === '' || fila.datos_transformados[campo] === undefined
                      ? '—'
                      : String(fila.datos_transformados[campo])}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </>
        ) : (
          <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>
            {fila.clasificacion === 'IGNORADO_EXISTENTE' && 'Ya registrado — no será importado.'}
            {fila.clasificacion === 'REINGRESO' && 'Empleado dado de baja — al aplicar quedará como pendiente para revisión de RH, sin reactivarse automáticamente.'}
            {fila.clasificacion === 'ERROR' && 'No se generó jerarquía resuelta por errores en la fila.'}
          </p>
        )}

        <p style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>
          Observaciones ({observaciones.length})
        </p>
        {observaciones.length === 0 ? (
          <p style={{ fontSize: '12px', color: '#9ca3af' }}>Sin observaciones.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {observaciones.map((o, i) => (
              <div key={i} style={{ fontSize: '11px', color: o.tipo === 'error' ? '#991b1b' : o.tipo === 'advertencia' ? '#92400e' : '#6b7280' }}>
                <strong>[{o.tipo}]</strong> {o.codigo ? <code>{o.codigo}</code> : null} {o.mensaje}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
